/**
 * Tree view component for displaying Encompass eFolder structure
 * Shows collapsible folders with nested document attachments
 * Supports grouped view (Groups > Folders > Documents) and flat view (Folders > Documents)
 */

import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, FileText, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { EfolderDocumentRow } from './EfolderDocumentRow';
import type {
  EncompassDocumentWithAttachments,
  EncompassAttachment,
  EncompassDocumentGroup,
  EfolderGroupingMode
} from '@/types/encompass-documents';

interface EfolderTreeViewProps {
  documents: EncompassDocumentWithAttachments[];
  groupedDocuments?: EncompassDocumentGroup[];
  groupingMode: EfolderGroupingMode;
  selectedAttachmentId: string | null;
  onSelectAttachment: (attachment: EncompassAttachment) => void;
  onViewAttachment: (attachment: EncompassAttachment) => void;
  onDownloadAttachment: (attachment: EncompassAttachment) => void;
  /** Whether copy mode is active */
  isCopyMode?: boolean;
  /** Set of checked attachment IDs */
  checkedAttachments?: Set<string>;
  /** Callback when checkbox is toggled */
  onToggleCheck?: (attachmentId: string) => void;
  /** Phase 6: Toggle all checkboxes in a group */
  onToggleGroupCheck?: (groupName: string) => void;
  /** Phase 6: Toggle all checkboxes in a folder */
  onToggleFolderCheck?: (folderId: string) => void;
  /** Phase 6: Get selection state for a group (none/partial/all) */
  getGroupSelectionState?: (groupName: string) => 'none' | 'partial' | 'all';
  /** Phase 6: Get selection state for a folder (none/partial/all) */
  getFolderSelectionState?: (folderId: string) => 'none' | 'partial' | 'all';
  /** Get count of copyable (downloaded + active) attachments for a group */
  getCopyableCountForGroup?: (groupName: string) => number;
  /** Current search query — threaded to rows for title highlighting + empty-state copy. */
  searchQuery?: string;
}

interface FolderNodeProps {
  document: EncompassDocumentWithAttachments;
  selectedAttachmentId: string | null;
  onSelectAttachment: (attachment: EncompassAttachment) => void;
  onViewAttachment: (attachment: EncompassAttachment) => void;
  onDownloadAttachment: (attachment: EncompassAttachment) => void;
  defaultExpanded?: boolean;
  /** Whether copy mode is active */
  isCopyMode?: boolean;
  /** Set of checked attachment IDs */
  checkedAttachments?: Set<string>;
  /** Callback when checkbox is toggled */
  onToggleCheck?: (attachmentId: string) => void;
  /** Phase 6: Toggle all checkboxes in this folder */
  onToggleFolderCheck?: (folderId: string) => void;
  /** Phase 6: Get selection state for this folder (none/partial/all) */
  getFolderSelectionState?: (folderId: string) => 'none' | 'partial' | 'all';
  /** Current search query — forwarded to each row for title highlighting. */
  searchQuery?: string;
}

function FolderNode({
  document,
  selectedAttachmentId,
  onSelectAttachment,
  onViewAttachment,
  onDownloadAttachment,
  defaultExpanded = false,
  isCopyMode = false,
  checkedAttachments,
  onToggleCheck,
  onToggleFolderCheck,
  getFolderSelectionState,
  searchQuery = ''
}: FolderNodeProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const hasAttachments = document.attachments.length > 0;
  const attachmentCount = document.attachments.length;

  // Count status
  const downloadedCount = document.attachments.filter(a => a.download_status === 'downloaded').length;
  const pendingCount = document.attachments.filter(a => a.download_status === 'pending').length;
  const failedCount = document.attachments.filter(a => a.download_status === 'failed').length;

  // Phase 6: Count copyable attachments (downloaded + active)
  const copyableCount = document.attachments.filter(
    a => a.download_status === 'downloaded' && a.is_active !== false
  ).length;

  // Phase 6: Get folder selection state
  const folderSelectionState = getFolderSelectionState?.(document.id) || 'none';

  return (
    <div className="mb-1">
      {/* Folder header - card style */}
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 cursor-pointer rounded-lg transition-all',
          'bg-card border border-transparent',
          'hover:border-border hover:shadow-sm',
          isExpanded && 'border-border shadow-sm bg-card/80',
          !hasAttachments && 'opacity-60'
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Expand/collapse icon */}
        <span className="text-muted-foreground flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>

        {/* Phase 6: Folder checkbox (copy mode only) */}
        {isCopyMode && (
          <Checkbox
            checked={
              folderSelectionState === 'all'
                ? true
                : folderSelectionState === 'partial'
                  ? 'indeterminate'
                  : false
            }
            disabled={copyableCount === 0}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFolderCheck?.(document.id);
            }}
            className="mr-1"
          />
        )}

        {/* Folder icon */}
        {isExpanded ? (
          <FolderOpen className="h-4 w-4 text-amber-500 flex-shrink-0" />
        ) : (
          <Folder className="h-4 w-4 text-amber-500 flex-shrink-0" />
        )}

        {/* Folder name */}
        <span className="flex-1 text-sm font-medium truncate">
          {formatFolderTitle(document.title)}
        </span>

        {/* Status indicators */}
        <div className="flex items-center gap-1.5">
          {downloadedCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-green-100 text-green-700 border-0">
              {downloadedCount}
            </Badge>
          )}
          {pendingCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-yellow-100 text-yellow-700 border-0">
              {pendingCount}
            </Badge>
          )}
          {failedCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-red-100 text-red-700 border-0">
              {failedCount}
            </Badge>
          )}
          {downloadedCount === 0 && pendingCount === 0 && failedCount === 0 && (
            <span className="text-xs text-muted-foreground">{attachmentCount}</span>
          )}
        </div>
      </div>

      {/* Attachments list (when expanded) */}
      {isExpanded && hasAttachments && (
        <div className="ml-4 mt-1 pl-4 border-l-2 border-muted">
          {document.attachments.map(attachment => (
            <EfolderDocumentRow
              key={attachment.id}
              attachment={attachment}
              isSelected={selectedAttachmentId === attachment.id}
              onSelect={onSelectAttachment}
              onView={onViewAttachment}
              onDownload={onDownloadAttachment}
              isCopyMode={isCopyMode}
              isChecked={checkedAttachments?.has(attachment.id) ?? false}
              onToggleCheck={onToggleCheck}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Formats folder title for display
 * Removes "00 - " prefix if present
 */
function formatFolderTitle(title: string): string {
  // Remove "00 - " or similar numbering prefixes
  return title.replace(/^\d+\s*[-–]\s*/, '');
}

// ============================================================================
// GroupNode - Renders a document group with its folders
// ============================================================================

interface GroupNodeProps {
  group: EncompassDocumentGroup;
  selectedAttachmentId: string | null;
  onSelectAttachment: (attachment: EncompassAttachment) => void;
  onViewAttachment: (attachment: EncompassAttachment) => void;
  onDownloadAttachment: (attachment: EncompassAttachment) => void;
  groupingMode: EfolderGroupingMode;
  defaultExpanded?: boolean;
  /** Whether copy mode is active */
  isCopyMode?: boolean;
  /** Set of checked attachment IDs */
  checkedAttachments?: Set<string>;
  /** Callback when checkbox is toggled */
  onToggleCheck?: (attachmentId: string) => void;
  /** Phase 6: Toggle all checkboxes in this group */
  onToggleGroupCheck?: (groupName: string) => void;
  /** Phase 6: Toggle all checkboxes in a folder */
  onToggleFolderCheck?: (folderId: string) => void;
  /** Phase 6: Get selection state for this group (none/partial/all) */
  getGroupSelectionState?: (groupName: string) => 'none' | 'partial' | 'all';
  /** Phase 6: Get selection state for a folder (none/partial/all) */
  getFolderSelectionState?: (folderId: string) => 'none' | 'partial' | 'all';
  /** Get count of copyable (downloaded + active) attachments for this group */
  getCopyableCountForGroup?: (groupName: string) => number;
  /** Current search query — forwarded to nested rows for highlighting. */
  searchQuery?: string;
}

function GroupNode({
  group,
  selectedAttachmentId,
  onSelectAttachment,
  onViewAttachment,
  onDownloadAttachment,
  groupingMode,
  defaultExpanded = false,
  isCopyMode = false,
  checkedAttachments,
  onToggleCheck,
  onToggleGroupCheck,
  onToggleFolderCheck,
  getGroupSelectionState,
  getFolderSelectionState,
  getCopyableCountForGroup,
  searchQuery = ''
}: GroupNodeProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // In groups-flat mode, show attachments directly without folders
  const showFolders = groupingMode === 'groups';

  // Get all attachments for groups-flat mode
  const allAttachments = showFolders
    ? []
    : group.folders.flatMap(folder => folder.attachments);

  // Phase 6: Get group selection state
  const groupSelectionState = getGroupSelectionState?.(group.groupName) || 'none';

  // Get copyable count - use provided function or fall back to downloadedCount
  // This ensures checkbox is disabled when all downloaded files are inactive
  const copyableCount = getCopyableCountForGroup?.(group.groupName) ?? group.downloadedCount;

  return (
    <div className="mb-2">
      {/* Group header */}
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2.5 cursor-pointer rounded-lg transition-all',
          'bg-muted/50 border border-transparent',
          'hover:border-border hover:shadow-sm',
          isExpanded && 'border-border shadow-sm bg-muted/70'
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Expand/collapse icon */}
        <span className="text-muted-foreground flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>

        {/* Phase 6: Group checkbox (copy mode only) */}
        {isCopyMode && (
          <Checkbox
            checked={
              groupSelectionState === 'all'
                ? true
                : groupSelectionState === 'partial'
                  ? 'indeterminate'
                  : false
            }
            disabled={copyableCount === 0}
            onClick={(e) => {
              e.stopPropagation();
              onToggleGroupCheck?.(group.groupName);
            }}
            className="mr-1"
          />
        )}

        {/* Group icon - blue to differentiate from folders */}
        <Layers className="h-4 w-4 text-blue-500 flex-shrink-0" />

        {/* Group name */}
        <span className="flex-1 text-sm font-semibold truncate">
          {group.displayName}
        </span>

        {/* Status indicators */}
        <div className="flex items-center gap-1.5">
          {group.downloadedCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-green-100 text-green-700 border-0">
              {group.downloadedCount}
            </Badge>
          )}
          {group.pendingCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-yellow-100 text-yellow-700 border-0">
              {group.pendingCount}
            </Badge>
          )}
          {group.failedCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-red-100 text-red-700 border-0">
              {group.failedCount}
            </Badge>
          )}
          {group.downloadedCount === 0 && group.pendingCount === 0 && group.failedCount === 0 && (
            <span className="text-xs text-muted-foreground">{group.totalAttachments}</span>
          )}
        </div>
      </div>

      {/* Group content (when expanded) */}
      {isExpanded && (
        <div className="ml-4 mt-1 pl-4 border-l-2 border-blue-200">
          {showFolders ? (
            // Groups mode: Show folders inside group
            group.folders.map((doc) => (
              <FolderNode
                key={doc.id}
                document={doc}
                selectedAttachmentId={selectedAttachmentId}
                onSelectAttachment={onSelectAttachment}
                onViewAttachment={onViewAttachment}
                onDownloadAttachment={onDownloadAttachment}
                defaultExpanded={false}
                isCopyMode={isCopyMode}
                checkedAttachments={checkedAttachments}
                onToggleCheck={onToggleCheck}
                onToggleFolderCheck={onToggleFolderCheck}
                getFolderSelectionState={getFolderSelectionState}
                searchQuery={searchQuery}
              />
            ))
          ) : (
            // Groups-flat mode: Show attachments directly
            allAttachments.map(attachment => (
              <EfolderDocumentRow
                key={attachment.id}
                attachment={attachment}
                isSelected={selectedAttachmentId === attachment.id}
                onSelect={onSelectAttachment}
                onView={onViewAttachment}
                onDownload={onDownloadAttachment}
                isCopyMode={isCopyMode}
                isChecked={checkedAttachments?.has(attachment.id) ?? false}
                onToggleCheck={onToggleCheck}
                searchQuery={searchQuery}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function EfolderTreeView({
  documents,
  groupedDocuments = [],
  groupingMode,
  selectedAttachmentId,
  onSelectAttachment,
  onViewAttachment,
  onDownloadAttachment,
  isCopyMode = false,
  checkedAttachments,
  onToggleCheck,
  onToggleGroupCheck,
  onToggleFolderCheck,
  getGroupSelectionState,
  getFolderSelectionState,
  getCopyableCountForGroup,
  searchQuery = ''
}: EfolderTreeViewProps) {
  // Determine what to render based on grouping mode
  const isGroupedMode = groupingMode === 'groups' || groupingMode === 'groups-flat';
  const hasContent = isGroupedMode
    ? groupedDocuments.length > 0
    : documents.length > 0;

  // Empty state — distinguish "no docs at all" from "search returned nothing"
  // so users know the search is also looking inside document contents.
  if (!hasContent) {
    const trimmed = searchQuery.trim();
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 text-center">
        <div className="rounded-full bg-muted/50 p-4 mb-4">
          <FileText className="h-10 w-10 text-muted-foreground/50" />
        </div>
        {trimmed ? (
          <>
            <p className="text-sm font-medium text-muted-foreground mb-1">No matches for "{trimmed}"</p>
            <p className="text-xs text-muted-foreground/70 max-w-xs">
              Searched file names, folders, and the text inside downloaded documents.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-muted-foreground mb-1">No documents found</p>
            <p className="text-xs text-muted-foreground/70">
              Try adjusting your filters
            </p>
          </>
        )}
      </div>
    );
  }

  // Auto-expand search results so matches aren't hidden inside collapsed folders.
  const autoExpand = searchQuery.trim().length > 0;

  // Grouped mode: Render groups
  if (isGroupedMode) {
    return (
      <div className="space-y-2">
        {groupedDocuments.map((group) => (
          <GroupNode
            key={group.groupName}
            group={group}
            groupingMode={groupingMode}
            selectedAttachmentId={selectedAttachmentId}
            onSelectAttachment={onSelectAttachment}
            onViewAttachment={onViewAttachment}
            onDownloadAttachment={onDownloadAttachment}
            defaultExpanded={autoExpand}
            isCopyMode={isCopyMode}
            checkedAttachments={checkedAttachments}
            onToggleCheck={onToggleCheck}
            onToggleGroupCheck={onToggleGroupCheck}
            onToggleFolderCheck={onToggleFolderCheck}
            getGroupSelectionState={getGroupSelectionState}
            getFolderSelectionState={getFolderSelectionState}
            getCopyableCountForGroup={getCopyableCountForGroup}
            searchQuery={searchQuery}
          />
        ))}
      </div>
    );
  }

  // Flat mode: Render folders directly (existing behavior)
  return (
    <div className="space-y-1">
      {documents.map((doc) => (
        <FolderNode
          key={doc.id}
          document={doc}
          selectedAttachmentId={selectedAttachmentId}
          onSelectAttachment={onSelectAttachment}
          onViewAttachment={onViewAttachment}
          onDownloadAttachment={onDownloadAttachment}
          defaultExpanded={autoExpand}
          isCopyMode={isCopyMode}
          checkedAttachments={checkedAttachments}
          onToggleCheck={onToggleCheck}
          onToggleFolderCheck={onToggleFolderCheck}
          getFolderSelectionState={getFolderSelectionState}
          searchQuery={searchQuery}
        />
      ))}
    </div>
  );
}
